/**
 * Folding the quoted history out of a reply (architecture §1): the thread's earlier messages are
 * already cards below, so a message shows only what is new in it, with "Show quoted text" to
 * see the rest. Three ways to find where the history starts, first match wins:
 *
 * 1. the containers mail clients wrap it in (Gmail, Outlook desktop/web/iOS, Apple Mail, Yahoo,
 *    Proton, Thunderbird);
 * 2. a reply header: "On … wrote:", or a "From: … Sent:/Date: …" block;
 * 3. the opening words of an earlier message in the thread, found in this one.
 *
 * The HTML is parsed with `DOMParser` into an inert document (nothing loads, no script runs)
 * that never joins the page; the kept part goes to the sandboxed frame like any email. If the cut
 * would leave nothing, the message is shown whole.
 */

const MARKERS = [
  "div.gmail_quote",
  "blockquote.gmail_quote",
  "div#appendonsend",
  "div#ms-outlook-mobile-body-separator-line",
  "div#mail-editor-reference-message-container",
  "div#divRplyFwdMsg",
  "div.OutlookMessageHeader",
  "hr#stopSpelling",
  "div.yahoo_quoted",
  "blockquote.protonmail_quote",
  "div.moz-cite-prefix",
  'blockquote[type="cite"]',
].join(", ");

const WROTE = /^\s*(On|Le|Am|El)\b.{4,300}\b(wrote|a écrit|schrieb|escribió)\s*:\s*$/is;
// No word boundary before "Sent": `<br>`-separated header lines run together in textContent.
const HEADER = /^\s*(From|De|Von)\s*:.{1,300}?(Sent|Date|Envoyé|Gesendet|Enviado)\s*:/is;
const BLOCKS = "p, div, blockquote, table, hr, li";

/** Whitespace collapsed, lower case: how texts are compared. */
const norm = (s: string) => s.replace(/\s+/g, " ").trim().toLowerCase();

/** The opening words of an earlier message worth searching for (short ones match too much). */
export function openingOf(text: string): string | null {
  const n = norm(text);
  if (n.length < 24) return null;
  return n.slice(0, 80);
}

/** Removes `node` and everything after it in document order. */
function cutFrom(node: Node, root: Node): void {
  let current: Node | null = node;
  while (current !== null && current !== root) {
    while (current.nextSibling !== null) current.nextSibling.remove();
    const parent: Node | null = current.parentNode;
    if (current === node) (current as ChildNode).remove();
    current = parent;
  }
}

/** Drops trailing rules, empty blocks and line breaks left behind by a cut. */
function trimTail(body: HTMLElement): void {
  for (;;) {
    const last = body.lastChild;
    if (last === null) return;
    const empty = last.nodeType === 3 ? norm(last.textContent ?? "") === "" : (last as Element).matches("hr, br") || ((last as Element).matches("div, p, span") && norm(last.textContent ?? "") === "" && (last as Element).querySelector("img") === null);
    if (!empty) return;
    last.remove();
  }
}

function headerBlock(body: HTMLElement): Element | null {
  for (const el of Array.from(body.querySelectorAll(BLOCKS))) {
    const text = el.textContent ?? "";
    if (text.length > 1200) continue;
    if (el.querySelector(BLOCKS) !== null && !HEADER.test(text)) continue;
    if (WROTE.test(text) || HEADER.test(text)) return el;
  }
  return null;
}

/** The block where an earlier message's opening words start, and the header just above it. */
function earlierStart(body: HTMLElement, openings: readonly string[]): Element | null {
  const doc = body.ownerDocument;
  const walker = doc.createTreeWalker(body, 4);
  let text = "";
  const starts: Array<{ at: number; node: Node }> = [];
  for (let n = walker.nextNode(); n !== null; n = walker.nextNode()) {
    const piece = (n.textContent ?? "").replace(/\s+/g, " ").toLowerCase();
    if (piece.trim() === "") continue;
    starts.push({ at: text.length, node: n });
    text += piece.startsWith(" ") || text.endsWith(" ") || text === "" ? piece : ` ${piece}`;
  }
  let best = -1;
  for (const o of openings) {
    const at = text.indexOf(o);
    if (at > 0 && (best === -1 || at < best)) best = at;
  }
  if (best === -1) return null;
  let hit: Node | null = null;
  for (const s of starts) {
    if (s.at > best) break;
    hit = s.node;
  }
  if (hit === null) return null;
  let block: Element | null = hit.parentElement === null ? null : hit.parentElement.closest(BLOCKS);
  if (block === null || block === body) return null;
  // A reply header or rule right above the quoted text goes too.
  for (let prev = block.previousElementSibling, steps = 0; prev !== null && steps < 4; prev = prev.previousElementSibling, steps++) {
    const t = prev.textContent ?? "";
    if (prev.matches("hr") || WROTE.test(t) || HEADER.test(t) || /^\s*(-{2,}\s*)?original message/i.test(t)) block = prev;
    else if (norm(t) !== "") break;
  }
  return block;
}

/** Where the quoted history starts: a client's container, else a reply header, else earlier words. */
function quoteStart(body: HTMLElement, openings: readonly string[]): Element | null {
  const marker = body.querySelector(MARKERS);
  if (marker !== null) return marker;
  const header = headerBlock(body);
  if (header !== null) return header;
  return earlierStart(body, openings);
}

export interface Folded {
  /** The message without its quoted history (or whole, when nothing was found). */
  latest: string;
  /** Whether anything was folded away. */
  folded: boolean;
}

export function foldQuotedHtml(html: string, earlierTexts: readonly string[]): Folded {
  const doc = new DOMParser().parseFromString(html, "text/html");
  const body = doc.body;
  const before = norm(body.textContent ?? "");
  const openings = earlierTexts.map(openingOf).filter((o): o is string => o !== null);
  const start = quoteStart(body, openings);
  if (start === null) return { latest: html, folded: false };
  cutFrom(start, body);
  trimTail(body);
  const after = norm(body.textContent ?? "");
  if (after === "" && body.querySelector("img") === null) return { latest: html, folded: false };
  if (after === before) return { latest: html, folded: false };
  const head = doc.head.innerHTML;
  return { latest: `<!doctype html><html><head>${head}</head><body>${body.innerHTML}</body></html>`, folded: true };
}

/** Plain-text replies: cut at "On … wrote:", a "From:/Sent:" header, "Original Message", or a trailing ">" block. */
export function foldQuotedText(text: string): Folded {
  const lines = text.split(/\r?\n/);
  let cut = -1;
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i] as string;
    const next = lines.slice(i, i + 5).join("\n");
    if (WROTE.test(line) || /^\s*-{2,}\s*original message\s*-{2,}/i.test(line) || (/^\s*(From|De|Von)\s*:/i.test(line) && HEADER.test(next)) || (/^\s*>/.test(line) && lines.slice(i).every((l) => /^\s*(>|$)/.test(l)))) {
      cut = i;
      break;
    }
  }
  if (cut <= 0) return { latest: text, folded: false };
  const latest = lines.slice(0, cut).join("\n").replace(/\s+$/, "");
  return latest.trim() === "" ? { latest: text, folded: false } : { latest, folded: true };
}

/** The plain text of the messages before `m` in its thread (text part, else the HTML's text). */
export function earlierTexts(messages: ReadonlyArray<{ id: string; date: Date; text: string | null; html: string | null }>, m: { id: string; date: Date }): string[] {
  return messages
    .filter((x) => x.id !== m.id && x.date.getTime() < m.date.getTime())
    .map((x) => (x.text !== null ? x.text : x.html !== null ? (new DOMParser().parseFromString(x.html, "text/html").body.textContent ?? "") : ""));
}
